// PlanerWorkspace — geführter 11-Schritt-Expertenworkflow (pvXpert-Ersatz).
// Reine Orchestrierungs-Shell im EnergyOS-Designsystem: Projektkontext-Leiste
// (4 KPIs) + horizontaler Stepper (done/active/future) + Ribbon-Slot + Inhalt
// (bestehende Tabs via renderTab) + Footer mit Freigabe-Blocker. Kein Duplizieren
// von Fachlogik — Schrittinhalte sind die vorhandenen Tab-Komponenten.
import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

export interface PlanerStep {
  /** interner Schlüssel (z. B. "anordnung") */
  key: string;
  /** Tab-Key, den renderTab rendert (z. B. "planning"); null für Gap-Inhalte */
  tabKey: string;
  num: string; // "01".."11"
  label: string;
  iconPath: string; // SVG path d
}

export interface PlanerKpi {
  label: string;
  value: string;
  tone?: "default" | "brand" | "primary";
}

interface PlanerWorkspaceProps {
  projectName: string;
  statusLabel: string;
  address?: string;
  angebotNr?: string;
  revisionLabel?: string;
  kpis: PlanerKpi[];
  /** abgeschlossene Schritte (key → true) */
  completed: Record<string, boolean>;
  /** Freigabe-Blocker: dieser Schritt + Folge-Schritte sind gesperrt, bis behoben */
  blocker?: { fromStepKey: string; reason: string } | null;
  /** rendert den Inhalt eines Schritts (bestehende Tab-Komponente) */
  renderStep: (step: PlanerStep) => ReactNode;
  /** optionale Ribbon-Aktionsleiste je Schritt */
  ribbonByStep?: Record<string, ReactNode>;
}

export const PLANER_STEPS: PlanerStep[] = [
  { key: "projekt", tabKey: "overview", num: "01", label: "Projekt", iconPath: "M7 3h8l4 4v14H7z M15 3v4h4" },
  { key: "dach", tabKey: "geospatial", num: "02", label: "Dach", iconPath: "M3 12l9-7 9 7 M5 11v9h14v-9" },
  { key: "module", tabKey: "komponenten", num: "03", label: "PV-Module", iconPath: "M3 5h18v14H3z M3 12h18 M9 5v14 M15 5v14" },
  { key: "konstr", tabKey: "konstruktion", num: "04", label: "Konstruktion", iconPath: "M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6 M12 2v3 M12 19v3 M3 12h3 M18 12h3" },
  { key: "anordnung", tabKey: "planning", num: "05", label: "Anordnung", iconPath: "M4 4h7v7H4z M13 4h7v7h-7z M4 13h7v7H4z M13 13h7v7h-7z" },
  { key: "cad", tabKey: "cadplan", num: "06", label: "CAD-Plan", iconPath: "M3 17L17 3l4 4L7 21z M14 6l4 4" },
  { key: "statik", tabKey: "structural", num: "07", label: "Statik", iconPath: "M12 3l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V6z M9 12l2 2 4-4" },
  { key: "edesign", tabKey: "pvanalyse", num: "08", label: "E-Designer", iconPath: "M13 3L5 13h6l-2 8 8-10h-6z" },
  { key: "elektrik", tabKey: "engineering", num: "09", label: "Elektrik", iconPath: "M9 2v7 M15 2v7 M7 9h10v3a5 5 0 0 1-10 0z M12 17v5" },
  { key: "ausgabe", tabKey: "costing", num: "10", label: "Ausgabe", iconPath: "M7 3h10v18H7z M10 8h4 M10 12h4 M10 16h3" },
  { key: "wirt", tabKey: "economics", num: "11", label: "Wirtschaftlichkeit", iconPath: "M4 20V4 M4 20h16 M8 16v-5 M12 16V8 M16 16v-7" },
];

function StepIcon({ d, color }: { d: string; color: string }) {
  return (
    <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      {d.split(" M").map((seg, i) => (
        <path key={i} d={i === 0 ? seg : `M${seg}`} />
      ))}
    </svg>
  );
}

const kpiColor = (tone?: PlanerKpi["tone"]) =>
  tone === "brand" ? "#16a34a" : tone === "primary" ? "#2563eb" : "#0e1c2e";

export function PlanerWorkspace(props: PlanerWorkspaceProps) {
  const [params, setParams] = useSearchParams();
  const steps = PLANER_STEPS;
  const urlStep = params.get("planerStep");
  const activeIdx = Math.max(
    0,
    steps.findIndex((s) => s.num === urlStep || s.key === urlStep),
  );
  const active = steps[activeIdx]!;

  const blockerIdx = props.blocker
    ? steps.findIndex((s) => s.key === props.blocker!.fromStepKey)
    : -1;

  const go = (idx: number) => {
    const s = steps[idx];
    if (!s) return;
    const next = new URLSearchParams(params);
    next.set("planerStep", s.num);
    setParams(next, { replace: true });
    // nach oben scrollen
    document.querySelector("[data-planer-scroll]")?.scrollTo?.({ top: 0 });
  };

  // Folge-Schritte nach dem Blocker sind gesperrt (z. B. Ausgabe/Freigabe).
  const isStepLocked = (idx: number) =>
    blockerIdx >= 0 && idx >= 10 /* Ausgabe(10)/Wirt(11) */ && !props.completed[props.blocker!.fromStepKey];

  return (
    <div data-planer-scroll style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* ── Projektkontext-Leiste ── */}
      <div
        style={{
          display: "flex",
          gap: 22,
          alignItems: "center",
          flexWrap: "wrap",
          padding: "18px 20px",
          background: "var(--color-surface,#fff)",
          border: "1px solid var(--color-border,#e2e8f0)",
          borderRadius: 14,
          boxShadow: "0 1px 3px rgba(15,23,42,.07)",
        }}
      >
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: "#0e1c2e", margin: 0 }}>{props.projectName}</h2>
            <span style={{ background: "#dbeafe", color: "#1e40af", borderRadius: 999, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
              {props.statusLabel}
            </span>
            {props.blocker ? (
              <span style={{ background: "#fef3c7", color: "#92400e", borderRadius: 999, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
                ⚠ {props.blocker.reason}
              </span>
            ) : (
              <span style={{ background: "#dcfce7", color: "#15803d", borderRadius: 999, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
                ✓ Freigabe möglich
              </span>
            )}
          </div>
          <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 5 }}>
            {props.address && <span>{props.address}</span>}
            {props.angebotNr && <span> · Angebot {props.angebotNr}</span>}
            {props.revisionLabel && <span> · {props.revisionLabel}</span>}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "stretch" }}>
          {props.kpis.map((k, i) => (
            <div key={k.label} style={{ padding: "2px 20px", borderLeft: i === 0 ? "none" : "1px solid #eef1f5" }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "#94a3b8" }}>{k.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2, fontVariantNumeric: "tabular-nums", color: kpiColor(k.tone), marginTop: 2 }}>{k.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── 11-Schritt-Stepper ── */}
      <div
        style={{
          display: "flex",
          overflowX: "auto",
          padding: "14px 12px",
          background: "var(--color-surface,#fff)",
          border: "1px solid var(--color-border,#e2e8f0)",
          borderRadius: 14,
          boxShadow: "0 1px 3px rgba(15,23,42,.07)",
        }}
      >
        {steps.map((s, i) => {
          const done = props.completed[s.key] && i !== activeIdx;
          const isActive = i === activeIdx;
          const locked = isStepLocked(i);
          const dotBg = isActive ? "#2563eb" : done ? "#16a34a" : "#fff";
          const dotBorder = isActive ? "#2563eb" : done ? "#16a34a" : "#cbd5e1";
          const iconColor = isActive || done ? "#fff" : locked ? "#cbd5e1" : "#94a3b8";
          const lineDone = i <= activeIdx ? "#16a34a" : "#e2e8f0";
          const labelColor = isActive ? "#1e293b" : done ? "#16a34a" : "#94a3b8";
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => go(i)}
              title={locked ? "Erst vorgelagerte Freigabe erledigen" : s.label}
              style={{
                flex: 1,
                minWidth: 96,
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 5,
                padding: "6px 2px",
                opacity: locked ? 0.55 : 1,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                <span style={{ flex: 1, height: 2, background: i === 0 ? "transparent" : lineDone }} />
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    background: dotBg,
                    border: `1.5px solid ${dotBorder}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: isActive ? "0 0 0 4px rgba(37,99,235,.14)" : "none",
                  }}
                >
                  {done ? (
                    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5 9-10" /></svg>
                  ) : (
                    <StepIcon d={s.iconPath} color={iconColor} />
                  )}
                </span>
                <span style={{ flex: 1, height: 2, background: i === steps.length - 1 ? "transparent" : (i < activeIdx ? "#16a34a" : "#e2e8f0") }} />
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color: isActive ? "#2563eb" : done ? "#16a34a" : "#cbd5e1" }}>{s.num}</span>
              <span style={{ fontSize: 11.5, fontWeight: isActive || done ? 700 : 600, color: labelColor, textAlign: "center", lineHeight: 1.2 }}>{s.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── Ribbon (optional) ── */}
      {props.ribbonByStep?.[active.key] && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>{props.ribbonByStep[active.key]}</div>
      )}

      {/* ── Schritt-Inhalt ── */}
      <div>
        {isStepLocked(activeIdx) ? (
          <div className="card" style={{ padding: 24, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, color: "#92400e", fontSize: 13, lineHeight: 1.6 }}>
            ⚠ Dieser Schritt ist gesperrt: <strong>{props.blocker?.reason}</strong>. Bitte zuerst den vorgelagerten
            Schritt „{steps[blockerIdx]?.label}" abschließen (Freigabe-/Risiko-Kopplung).
          </div>
        ) : (
          props.renderStep(active)
        )}
      </div>

      {/* ── Footer-Navigation ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 8, paddingTop: 18, borderTop: "1px solid var(--color-border,#e2e8f0)" }}>
        <button
          type="button"
          className="btn-secondary"
          disabled={activeIdx === 0}
          onClick={() => go(activeIdx - 1)}
          style={{ fontSize: 13, opacity: activeIdx === 0 ? 0.5 : 1 }}
        >
          ← Zurück
        </button>
        <div style={{ flex: 1, textAlign: "center", fontSize: 12.5, color: "#64748b" }}>
          Schritt {activeIdx + 1} von {steps.length} · {active.label}
        </div>
        {activeIdx < steps.length - 1 ? (
          (() => {
            const nextBlocked = blockerIdx >= 0 && activeIdx + 1 >= 10 && !props.completed[props.blocker!.fromStepKey];
            return (
              <button
                type="button"
                onClick={() => (nextBlocked ? go(blockerIdx) : go(activeIdx + 1))}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 14px",
                  cursor: "pointer",
                  background: nextBlocked ? "#f59e0b" : "#2563eb",
                }}
              >
                {nextBlocked ? `${props.blocker?.reason} zuerst` : `Weiter: ${steps[activeIdx + 1]!.label}`}
              </button>
            );
          })()
        ) : (
          <button type="button" style={{ fontSize: 13, fontWeight: 600, color: "#fff", border: "none", borderRadius: 6, padding: "8px 14px", cursor: "pointer", background: "#16a34a" }}>
            Angebot abschließen
          </button>
        )}
      </div>
    </div>
  );
}
